// Analytics & Performance (/analytics) - the one analytics page of the
// coordination suite. It folds together what used to be three pages:
//
//   Analytics Overview  -> KPI row, issues by status, recommendations,
//                          latest exports / recent issues
//   Contributors        -> the contributor activity cards (heatmap / bars,
//                          Team / Individuals), in that page's styling
//   Team Performance    -> Team Insights (scored PSI tiles) + one dense
//                          team table (activity-share estimates, schedule)
//
// One filter card drives everything: it merges the overview's filters
// (time window, projects, disciplines, milestones, users, revisions) with
// the contributor page's (project, time, user pills, Team/Individuals,
// Bars/Heatmap). Data: GET /analytics/overview, /analytics/contributions,
// /analytics/recommendations (all filter-aware), GET /suggestions for the
// insights, plus the company user list for the roster.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Empty, Segmented, Select, Skeleton, Table, Tag, Tooltip } from "antd";
import {
  BarChartOutlined, BulbOutlined, FileZipOutlined, HeatMapOutlined, ProjectOutlined,
  RightOutlined, RiseOutlined, TeamOutlined, UserOutlined, WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { url, tokenRef } from "api";
import { getDiscipline } from "api/discipline/getDiscipline";
import { getMileStones } from "api/milestones/getMilestones";
import { getUsers } from "api/users/getUsers";
import { getRevision } from "api/Revision/getRevision";
import {
  getCompanyOverview, getCompanyContributions, getCompanyRecommendations,
} from "api/analytics/projectAnalyticsApi";
import { getRememberedProject, rememberProject } from "helpers/projectSelection";
import { issuePath } from "helpers/issueLinks";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import {
  Card, Stat, Pill, StatusBreakdown, PriorityBreakdown, RecoBucket, T,
} from "components/Dashboard/CompanyAnalyticsPanel";
import { ContributorCard, TeamStackedBars, colorFor } from "components/Analytics/ContributorCards";
import { TeamInsightsSection } from "pages/TeamInsights/TeamInsightsPage";
import "components/GlobalFilterBar/GlobalFilterBar.scss";
import "./AnalyticsPerformancePage.css";
import { usePreference } from "helpers/userPrefs";
import { matchOption } from "components/SearchSelect/searchSelect";

const TIME_PILLS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 180, label: "Last 180 days" },
  { days: 365, label: "Last year" },
];

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}
const asList = (x, ...keys) => {
  if (Array.isArray(x)) return x;
  for (const k of keys) if (Array.isArray(x?.[k])) return x[k];
  return [];
};
const fullName = (u) => [u?.name, u?.lastName].filter(Boolean).join(" ") || u?.email || "Unknown";

// --- Team table estimates (moved from the Team Performance page) ---------
// Problem Solver Index, rule-based: (issues resolved x complexity) / avg time,
// clamped to 0..100. Client-side estimate from each person's share of
// activity; the scored PSI in Team Insights is the authoritative one.
function computePSI({ resolved, activeDays, windowDays, avgHours }) {
  if (!resolved || resolved <= 0) return 0;
  const complexity = Math.max(0.3, Math.min(1.5, (activeDays || 0) / Math.max(1, windowDays) * 1.4));
  const hours = Math.max(0.5, avgHours || 4);
  const raw = (resolved * complexity * 25) / hours;
  return Math.max(0, Math.min(100, Math.round(raw)));
}
function scheduleStatus(row) {
  if (row.psi >= 85 && row.doneRatio >= 0.9) return { key: "ahead", label: "Ahead", color: T.added2, bg: T.addedBg };
  if (row.psi >= 70 && row.doneRatio >= 0.7) return { key: "track", label: "On track", color: T.added2, bg: T.addedBg };
  if (row.psi >= 50) return { key: "slight", label: "Slight delay", color: "#2563EB", bg: "#EFF6FF" };
  return { key: "behind", label: "Behind", color: T.removed2, bg: T.removedBg };
}
const AVATAR_COLORS = ["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#14B8A6", "#6366F1"];
function avatarColor(id) {
  const s = String(id || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
const psiColor = (psi) => (psi >= 85 ? T.added2 : psi >= 70 ? "#2563EB" : psi >= 50 ? "#B45309" : T.removed2);

export default function AnalyticsPerformancePage() {
  const history = useHistory();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const company = userInfoRedux?.company;
  const currentUserId = userInfoRedux?._id || userInfoRedux?.id;

  // ---- filters ----
  // Period and chart modes remembered per person (helpers/userPrefs).
  const [days, setDays] = usePreference("analytics.days", 90);
  const [projectIds, setProjectIds] = useState(() => {
    const remembered = getRememberedProject(currentUserId);
    return remembered ? [remembered] : [];
  });
  const [disciplineIds, setDisciplineIds] = useState([]);
  const [milestoneIds, setMilestoneIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [revisionIds, setRevisionIds] = useState([]);
  const [viewMode, setViewMode] = usePreference("analytics.viewMode", "individuals");
  const [chartStyle, setChartStyle] = usePreference("analytics.chartStyle", "heatmap");

  // ---- pick lists ----
  const [projects, setProjects] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [users, setUsers] = useState([]);
  const [revisions, setRevisions] = useState([]);

  useEffect(() => {
    if (!company) return;
    let cancelled = false;
    Promise.all([
      fetch(`${url}/projects/picker`, { headers: authHeader() }).then((r) => r.json()).catch(() => ({ projects: [] })),
      getDiscipline(company).catch(() => []),
      getMileStones(company).catch(() => []),
      getUsers(company).catch(() => []),
      getRevision(company).catch(() => []),
    ]).then(([projs, disc, mile, usrs, revs]) => {
      if (cancelled) return;
      const list = asList(projs, "projects", "data");
      setProjects(list);
      setProjectIds((prev) => prev.filter((id) => list.some((p) => String(p._id) === String(id))));
      setDisciplines(asList(disc, "data"));
      setMilestones(asList(mile, "data"));
      setUsers(asList(usrs, "data", "users"));
      setRevisions(asList(revs, "data"));
    });
    return () => { cancelled = true; };
  }, [company]);

  // ---- analytics data ----
  const [overview, setOverview] = useState(null);
  const [contrib, setContrib] = useState(null);
  const [recos, setRecos] = useState(null);
  const [loading, setLoading] = useState(true);
  const projKey = projectIds.join(","), discKey = disciplineIds.join(","),
        mileKey = milestoneIds.join(","), userKey = userIds.join(","), revKey = revisionIds.join(",");
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const filters = { projectIds, disciplineIds, milestoneIds, userIds, revisionIds };
    // Each call fails soft: a user holding only one of the analytics
    // permissions still gets the sections their keys allow.
    Promise.all([
      getCompanyOverview(filters).catch(() => null),
      getCompanyContributions(days, filters).catch(() => null),
      getCompanyRecommendations(filters).catch(() => null),
    ]).then(([ov, co, rc]) => {
      if (cancelled) return;
      setOverview(ov); setContrib(co); setRecos(rc);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, projKey, discKey, mileKey, userKey, revKey]);

  // ---- derived ----
  const dayList = contrib?.days || [];
  const contributors = contrib?.contributors || [];
  // Contributor cards want [{date,count}] per person (the Contributors
  // page shape); the company feed gives {day: count} over `days`.
  const entries = useMemo(() => contributors.map((c) => ({
    user: c.user,
    commits: c.total,
    max: c.maxDay,
    activeDays: c.activeDays,
    daily: dayList.map((d) => ({ date: d, count: c.daily?.[d] || 0 })),
  })), [contributors, dayList]);
  const summary = useMemo(() => {
    if (!contrib) return null;
    const total = contributors.reduce((s, c) => s + (c.total || 0), 0);
    const perDay = {};
    contributors.forEach((c) => dayList.forEach((d) => { perDay[d] = (perDay[d] || 0) + (c.daily?.[d] || 0); }));
    const active = dayList.filter((d) => perDay[d] > 0).length;
    const max = Math.max(0, ...Object.values(perDay));
    return { total, avg: (total / Math.max(1, dayList.length)).toFixed(1), max, activeDays: active, count: contributors.length };
  }, [contrib, contributors, dayList]);

  // Team table: company roster + partner people who only appear in the
  // contributions feed, with the activity-share estimates.
  const rows = useMemo(() => {
    const windowDays = days;
    const byId = new Map(contributors.map((c) => [String(c.user?._id), c]));
    const known = new Set(users.map((u) => String(u._id)));
    const roster = [...users, ...contributors.map((c) => c.user).filter((u) => u && !known.has(String(u._id)))];
    const totalEvents = contributors.reduce((s, x) => s + (x.total || 0), 0) || 1;
    const resolvedSignal = overview?.issues?.byStatus?.resolved || 0;
    return roster.map((u) => {
      const c = byId.get(String(u._id));
      const total = c?.total || 0, activeDays = c?.activeDays || 0;
      const resolved = Math.max(0, Math.round((total / totalEvents) * resolvedSignal));
      const avgHours = resolved > 0 ? Math.max(1.5, 10 / Math.max(1, activeDays)) : null;
      const psi = computePSI({ resolved, activeDays, windowDays, avgHours });
      const assigned = Math.max(resolved, Math.round(total / 8));
      const done = Math.min(assigned, resolved + Math.round(activeDays / 2));
      return {
        id: String(u._id), name: fullName(u), role: u.role || u.title || "Member",
        total, activeDays, resolved, avgHours, psi,
        elemsPerWeek: Math.round(total / Math.max(1, windowDays / 7)),
        doneRatio: activeDays / windowDays, assigned, done,
      };
    }).sort((a, b) => b.psi - a.psi || b.total - a.total);
  }, [users, contributors, overview, days]);
  const pulse = useMemo(() => {
    const active = rows.filter((r) => r.total > 0);
    const avgPsi = active.length ? Math.round(active.reduce((s, r) => s + r.psi, 0) / active.length) : 0;
    const buckets = { ahead: 0, track: 0, slight: 0, behind: 0 };
    rows.forEach((r) => { buckets[scheduleStatus(r).key] += 1; });
    return { members: rows.length, active: active.length, avgPsi, ...buckets };
  }, [rows]);

  // Options
  const projectOptions = useMemo(() => projects
    .map((p) => ({ value: p._id, label: p.title || "Untitled" }))
    .sort((a, b) => a.label.localeCompare(b.label)), [projects]);
  const disciplineOptions = useMemo(() => disciplines.map((d) => ({ value: d._id, label: [d.key, d.value].filter(Boolean).join(" ") })), [disciplines]);
  const milestoneOptions = useMemo(() => milestones.map((m) => ({ value: m._id, label: [m.key, m.value].filter(Boolean).join(" ") })), [milestones]);
  const revisionOptions = useMemo(() => {
    const allowed = projectIds.length ? new Set(projectIds.map(String)) : null;
    const groups = new Map();
    revisions.forEach((r) => {
      const pid = String(r.projectId?._id || r.projectId || "__none__");
      if (allowed && !allowed.has(pid)) return;
      const title = r.projectId?.title || "No project";
      if (!groups.has(pid)) groups.set(pid, { label: title, options: [] });
      const label = [r.revisionNumber != null ? `r${r.revisionNumber}` : r.key, r.description].filter(Boolean).join(" · ");
      groups.get(pid).options.push({ value: r._id, label: label || String(r._id).slice(-6) });
    });
    return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [revisions, projectIds]);
  // User pills: everyone active in the window first (the contributor page
  // showed exactly those), then a select for the rest of the company.
  const activeUsers = useMemo(() => contributors.map((c) => c.user).filter(Boolean), [contributors]);
  const otherUserOptions = useMemo(() => {
    const active = new Set(activeUsers.map((u) => String(u._id)));
    return users.filter((u) => !active.has(String(u._id))).map((u) => ({ value: String(u._id), label: fullName(u) }));
  }, [users, activeUsers]);
  const toggleUser = (id) => setUserIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const hasFilters = projectIds.length || disciplineIds.length || milestoneIds.length || userIds.length || revisionIds.length || days !== 90;
  const reset = () => { setProjectIds([]); setDisciplineIds([]); setMilestoneIds([]); setUserIds([]); setRevisionIds([]); setDays(90); };

  const insightsProjectId = projectIds[0] || getRememberedProject(currentUserId) || projects[0]?._id || "";
  const reco = recos?.recommendations || { urgent: [], recommended: [], next: [] };
  const hasAnyReco = reco.urgent.length + reco.recommended.length + reco.next.length > 0;

  const teamColumns = [
    { title: "Member", dataIndex: "name", key: "name", sorter: (a, b) => a.name.localeCompare(b.name), width: 220,
      render: (v, r) => (
        <span className="ap-member">
          <span className="ap-avatar" style={{ background: avatarColor(r.id) }}>{v.charAt(0).toUpperCase()}</span>
          <span className="ap-member__text"><span className="ap-member__name">{v}</span><span className="ap-member__role">{r.role}</span></span>
        </span>) },
    { title: <Tooltip title="Problem Solver Index, estimated from this person's share of activity. Team Insights above carries the scored PSI.">PSI (est.)</Tooltip>,
      dataIndex: "psi", key: "psi", align: "right", defaultSortOrder: "descend", sorter: (a, b) => a.psi - b.psi,
      render: (v) => <b style={{ color: psiColor(v) }}>{v}</b> },
    { title: "Resolved", dataIndex: "resolved", key: "resolved", align: "right", sorter: (a, b) => a.resolved - b.resolved },
    { title: "Avg time", dataIndex: "avgHours", key: "avg", align: "right", sorter: (a, b) => (a.avgHours || 0) - (b.avgHours || 0),
      render: (v) => (v != null ? `${v.toFixed(1)}h` : "-") },
    { title: "Events", dataIndex: "total", key: "total", align: "right", sorter: (a, b) => a.total - b.total },
    { title: "Elem/wk", dataIndex: "elemsPerWeek", key: "epw", align: "right", sorter: (a, b) => a.elemsPerWeek - b.elemsPerWeek },
    { title: "Active days", dataIndex: "activeDays", key: "ad", align: "right", sorter: (a, b) => a.activeDays - b.activeDays,
      render: (v) => `${v}/${days}` },
    { title: "Schedule", key: "schedule", sorter: (a, b) => a.doneRatio - b.doneRatio, width: 190,
      render: (_, r) => { const s = scheduleStatus(r); return (
        <span className="ap-sched" style={{ color: s.color, background: s.bg }}>{s.label} · {r.done}/{r.assigned} tasks</span>); } },
  ];

  return (
    <div className="ap">
      <CoordinationBreadcrumbs current="Analytics & Performance" />
      <div className="ap-title">
        <h2>Analytics &amp; Performance</h2>
        <div className="ap-title__sub">
          Company-wide issues, contributor activity and team performance across all accessible projects.
        </div>
      </div>

      {/* ---- one filter card (overview filters + contributor filters) ---- */}
      <div className="global-filter-bar ap-filters">
        <div className="gfb-header">
          <div className="gfb-project">
            <span className="gfb-prefix">Viewing:</span>
            <Select filterOption={matchOption} className="gfb-project-select ap-filters__projects" mode="multiple" allowClear showSearch
              optionFilterProp="label" placeholder="All projects" maxTagCount="responsive"
              value={projectIds} options={projectOptions}
              onChange={(v) => {
                setProjectIds(v);
                if (v.length) rememberProject(currentUserId, v[v.length - 1]);
                if (v.length) {
                  const allowed = new Set(v.map(String));
                  setRevisionIds((prev) => prev.filter((rid) => {
                    const rev = revisions.find((r) => String(r._id) === String(rid));
                    return rev && allowed.has(String(rev.projectId?._id || rev.projectId));
                  }));
                }
              }} />
          </div>
          <div className="gfb-counters">
            {overview ? <>
              {overview.projectCount || 0} project{overview.projectCount === 1 ? "" : "s"}
              {" · "}{overview.memberCount || 0} member{overview.memberCount === 1 ? "" : "s"}
              {" · "}{overview.activityLast30Days || 0} events in last 30 days
            </> : null}
          </div>
          {hasFilters ? <button className="gfb-reset" onClick={reset}>Reset</button> : null}
        </div>

        <div className="gfb-row">
          <span className="gfb-label">Time</span>
          {TIME_PILLS.map((t) => (
            <button key={t.days} className={`gfb-pill ${days === t.days ? "gfb-pill-active" : ""}`} onClick={() => setDays(t.days)}>
              {t.label}
            </button>
          ))}
        </div>

        {(activeUsers.length > 0 || otherUserOptions.length > 0) && (
          <div className="gfb-row">
            <span className="gfb-label">Users</span>
            {activeUsers.map((u) => (
              <button key={u._id} className={`gfb-pill ${userIds.includes(String(u._id)) ? "gfb-pill-active" : ""}`}
                      onClick={() => toggleUser(String(u._id))}>
                {fullName(u)}
              </button>
            ))}
            {otherUserOptions.length > 0 && (
              <Select filterOption={matchOption} mode="multiple" size="small" allowClear showSearch optionFilterProp="label"
                placeholder={activeUsers.length ? "Other people" : "Anyone"} maxTagCount="responsive"
                className="ap-filters__select"
                value={userIds.filter((id) => otherUserOptions.some((o) => o.value === id))}
                onChange={(v) => setUserIds((prev) => [...prev.filter((id) => !otherUserOptions.some((o) => o.value === id)), ...v])}
                options={otherUserOptions} />
            )}
          </div>
        )}

        <div className="gfb-row">
          <span className="gfb-label">Scope</span>
          <Select mode="multiple" size="small" allowClear placeholder="All disciplines" maxTagCount="responsive"
            className="ap-filters__select" value={disciplineIds} onChange={setDisciplineIds} options={disciplineOptions} />
          <Select mode="multiple" size="small" allowClear placeholder="All milestones" maxTagCount="responsive"
            className="ap-filters__select" value={milestoneIds} onChange={setMilestoneIds} options={milestoneOptions} />
          <Select filterOption={matchOption} mode="multiple" size="small" allowClear showSearch optionFilterProp="label" placeholder="All revisions" maxTagCount="responsive"
            className="ap-filters__select ap-filters__select--wide" value={revisionIds} onChange={setRevisionIds} options={revisionOptions} />
        </div>

        <div className="gfb-row" style={{ marginBottom: 0 }}>
          <span className="gfb-label">Activity</span>
          <Segmented size="small" value={viewMode} onChange={setViewMode}
            options={[{ value: "team", label: "Team", icon: <TeamOutlined /> }, { value: "individuals", label: "Individuals", icon: <UserOutlined /> }]} />
          <Segmented size="small" value={chartStyle} onChange={setChartStyle}
            options={[{ value: "bars", label: "Bars", icon: <BarChartOutlined /> }, { value: "heatmap", label: "Heatmap", icon: <HeatMapOutlined /> }]} />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 16 }}><Skeleton active paragraph={{ rows: 8 }} /></div>
      ) : (
        <>
          {/* ---- overview ---- */}
          {overview && (
            <>
              <div className="ap-kpis">
                <Card style={{ padding: 14 }}>
                  <Stat label="Total issues" value={overview?.issues?.total || 0} icon={<FileZipOutlined style={{ color: T.navy, fontSize: 18 }} />} />
                </Card>
                <Card style={{ padding: 14 }}>
                  <Stat label="Open + Pending" color="#B45309" value={(overview?.issues?.byStatus?.open || 0) + (overview?.issues?.byStatus?.pending || 0)} />
                </Card>
                <Card style={{ padding: 14 }}>
                  <Stat label="Exports (30d)" color={T.added2} value={overview?.exports?.last30Days || 0} />
                </Card>
                <Card style={{ padding: 14 }}>
                  <Stat label="Active projects" value={overview?.projectCount || 0} icon={<ProjectOutlined style={{ color: T.navyD, fontSize: 18 }} />} />
                </Card>
                <Card style={{ padding: 14 }}>
                  <Stat label={`Events (${days}d)`} color={T.purple} value={summary?.total || 0} />
                </Card>
                <Card style={{ padding: 14 }}>
                  <Stat label="Contributors" value={summary?.count || 0} icon={<TeamOutlined style={{ color: T.navyD, fontSize: 18 }} />} />
                </Card>
              </div>

              <div className="ap-two">
                <Card title="Issues by status">
                  <div style={{ marginBottom: 12 }}><StatusBreakdown counts={overview?.issues?.byStatus} /></div>
                  <div className="ap-label">BY PRIORITY</div>
                  <PriorityBreakdown counts={overview?.issues?.byPriority} issues={overview?.issues?.byPriorityIssues} />
                </Card>
                <Card title="Recommendations" extra={<Pill kind="neutral" size="sm">Rule-based</Pill>}>
                  <RecoBucket title="Urgent" icon={<WarningOutlined />} color={T.removed2} bg={T.removedBg} items={reco.urgent} />
                  <RecoBucket title="Recommended" icon={<BulbOutlined />} color={"#8A6112"} bg={T.ambBg} items={reco.recommended} />
                  <RecoBucket title="Next action" icon={<RightOutlined />} color={T.navyD} bg={T.wash} items={reco.next} />
                  {!hasAnyReco && <Empty description="No action items" />}
                  {recos?.disclaimer && <div className="ap-disclaimer">{recos.disclaimer}</div>}
                </Card>
              </div>
            </>
          )}

          {/* ---- contributor activity (Contributors page styling) ---- */}
          <Card
            title={<>
              <span>Contributor activity</span>
              <span className="ap-card-hint">events per day · excluding merge commits</span>
            </>}
            style={{ marginBottom: 16 }}
          >
            {summary && (
              <div className="ap-summary">
                <strong>Team of {summary.count}</strong>
                <span><strong>{summary.total}</strong> total events</span>
                <span>· avg <strong>{summary.avg}</strong>/day</span>
                <span>· max <strong>{summary.max}</strong></span>
                <span>· active <strong>{summary.activeDays}</strong> of {dayList.length} days</span>
              </div>
            )}
            {!contrib || entries.length === 0 ? (
              <Empty description="No contributor activity in the selected window" />
            ) : viewMode === "team" ? (
              <div>
                <TeamStackedBars byUser={entries} unit="events" />
                <div className="ap-legend">
                  {entries.map((u, i) => (
                    <div key={u.user._id} className="ap-legend__item">
                      <span style={{ background: colorFor(i) }} />
                      {fullName(u.user)} · {u.commits}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="ap-contrib-grid">
                {entries.map((entry, i) => (
                  <ContributorCard key={entry.user._id} entry={entry} unit="events"
                                   color={colorFor(i)} view={chartStyle}
                                   onOpen={() => history.push(`/analytics/user/${entry.user._id}`)} />
                ))}
              </div>
            )}
          </Card>

          {/* ---- team performance ---- */}
          <Card style={{ marginBottom: 16 }}>
            <TeamInsightsSection embedded projectId={insightsProjectId} projects={projects}
              onProjectChange={(pid) => setProjectIds(pid ? [pid] : [])} />
          </Card>

          <Card
            title={<>
              <span>Team performance</span>
              <span className="ap-card-hint">last {days} days · activity-share estimates · click a row for their analytics</span>
            </>}
            style={{ marginBottom: 16 }}
          >
            <div className="ap-pulse">
              <div><span className="ap-pulse__n">{pulse.members}</span><span className="ap-pulse__l">members</span></div>
              <div><span className="ap-pulse__n">{pulse.active}</span><span className="ap-pulse__l">active</span></div>
              <div><span className="ap-pulse__n" style={{ color: psiColor(pulse.avgPsi) }}>{pulse.avgPsi}</span><span className="ap-pulse__l">avg PSI (active)</span></div>
              <div><span className="ap-pulse__n" style={{ color: T.added2 }}>{pulse.ahead + pulse.track}</span><span className="ap-pulse__l">ahead / on track</span></div>
              <div><span className="ap-pulse__n" style={{ color: "#2563EB" }}>{pulse.slight}</span><span className="ap-pulse__l">slight delay</span></div>
              <div><span className="ap-pulse__n" style={{ color: T.removed2 }}>{pulse.behind}</span><span className="ap-pulse__l">behind</span></div>
            </div>
            {rows.length === 0 ? <Empty description="No team members yet" /> : (
              <Table size="small" rowKey="id" columns={teamColumns} dataSource={rows}
                     pagination={false} scroll={{ y: 420, x: 900 }} className="ap-table"
                     onRow={(r) => ({ onClick: () => history.push(`/analytics/user/${r.id}`), style: { cursor: "pointer" } })} />
            )}
            <div className="ap-psi-note">
              <strong>PSI</strong> = (issues resolved × complexity) / avg time. Estimated here from each person's share
              of activity; a signal for coaching, not a verdict. The scored PSI in Team Insights is authoritative.
            </div>
          </Card>

          {/* ---- recent ---- */}
          {overview && (
            <div className="ap-two">
              <Card title="Latest exports">
                {(overview?.latestExports || []).length === 0 ? <Empty description="No exports yet" /> : (
                  <div className="ap-list">
                    {overview.latestExports.map((e) => (
                      <div key={e.id} className="ap-list__row">
                        <FileZipOutlined style={{ color: T.navy }} />
                        <div className="ap-list__text">
                          <div className="ap-list__title">{e.title || "Export"}</div>
                          <div className="ap-list__meta">
                            {moment(e.date).fromNow()}{e.author ? ` · ${e.author.name}` : ""}{e.milestone ? ` · ${e.milestone}` : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              <Card title="Recent issues">
                {(overview?.recentIssues || []).length === 0 ? <Empty description="No issues yet" /> : (
                  <div className="ap-list">
                    {overview.recentIssues.map((m) => (
                      <div key={m.id} className="ap-list__row" style={{ cursor: "pointer" }} onClick={() => history.push(issuePath(m))}>
                        <div className="ap-list__text">
                          <div className="ap-list__title">
                            {m.label && <span className="ap-list__label">{m.label}</span>}{m.title}
                          </div>
                          <div className="ap-list__meta">
                            {moment(m.updatedAt).fromNow()}{m.projectTitle ? ` · ${m.projectTitle}` : ""}{m.author?.name ? ` · ${m.author.name}` : ""}
                          </div>
                        </div>
                        <Tag color={m.status === "resolved" ? "green" : m.status === "pending" ? "orange" : m.status === "abandoned" ? "default" : "blue"}
                             style={{ margin: 0, fontSize: 10 }}>
                          {(m.status || "open").toUpperCase()}
                        </Tag>
                        {m.priority === "urgent" && <Pill kind="removed" size="sm"><RiseOutlined /> URGENT</Pill>}
                        <Link to={issuePath(m)} className="ap-view-btn" onClick={(e) => e.stopPropagation()}>View issue</Link>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
